import { useLazyQuery } from '@apollo/client';
import { useMemo } from 'react';
import {
	UserGroupRole, UserAccountLevel, RedditGroupsForUsersQueryVariables, RedditGroupsForUsersDocument,
} from 'wb-reddit/graphql/generated';
import {
	AsyncComboboxAutocomplete, handleErrorNotification, isReddit,
} from '@aleph/shared-components';
import { ComboboxAutocompleteOption } from 'wb-reddit/app/common/components/comboboxAutocompleteOption';
import { MutatingRedditUser, UserGroupOption } from 'wb-reddit/app/routes/users/mutator/utils';
import debounce from 'debounce-promise';
import useUserMetaData from 'wb-reddit/app/common/hooks/useUserMetaData';
import { useTranslation } from 'react-i18next';
import { AuthGraphGroupsForUsersDocument } from 'wb-reddit/graphql/authgraph/generated';
import { captureException } from '@sentry/react';

interface IProps {
	disabled: boolean;
	onOptionClick: (option: UserGroupOption) => void;
	values: MutatingRedditUser;
}

function UsersTeamsAutocomplete({
	disabled, onOptionClick, values,
}: IProps) {
	const { t } = useTranslation();

	const currentPlatformUser = useUserMetaData();

	const [searchTeams] = useLazyQuery(
		isReddit()
			? RedditGroupsForUsersDocument
			: AuthGraphGroupsForUsersDocument,
	);

	const debouncedSearchTeams = useMemo(() => debounce(
		async (variables: RedditGroupsForUsersQueryVariables) => searchTeams({
			variables,
		}),
		300,
	), []);

	const loadGroupOptions = async (search: string) => {
		const isPlatformAdmin = currentPlatformUser?.userAccountLevel === UserAccountLevel.Admin;

		const { data, error } = await debouncedSearchTeams({
			search:            `*${search}*`,
			// @ts-ignore: userEmail is set on the BE as any, it complains that userEmail is never nullish
			emailSearch:       `*@${(values.userEmail ?? '').split('@')[1]}` ?? '',
			userId:            isPlatformAdmin ? undefined : [{ value: [currentPlatformUser?.userId ?? ''] }],
			userGroupUserRole: isPlatformAdmin ? undefined : [{ value: [UserGroupRole.Admin, UserGroupRole.Owner] }],
			selectedIds:       values.userGroups?.map(
				(curr) => curr.value,
			) ?? [],
		});

		if (error) {
			captureException(error);

			handleErrorNotification(
				error,
				t(
					're: something went wrong while searching for teams',
					'Something went wrong while searching for teams',
				),
			);

			return [];
		}

		return data?.userGroups?.items ?? [];
	};

	return (
		<AsyncComboboxAutocomplete
			inputProps={{
				placeholder: t('re: user teams autocomplete placeholder', 'Add teams...'),
			}}
			size="large"
			loadOptions={loadGroupOptions}
			disabled={disabled}
			renderOption={({ option, searchQuery, onSelected }) => (
				<ComboboxAutocompleteOption
					option={option}
					onOptionClick={onOptionClick}
					onSelected={onSelected}
					searchQuery={searchQuery}
				/>
			)}
		/>
	);
}

export default UsersTeamsAutocomplete;
